import axios from "axios";

const BASE = "/api/v1/crm";

export function parseCrmError(err) {
  const data = (err && err.response && err.response?.data) || {};
  const code = data.code || "unknown";
  return {
    code,
    i18nKey: `crm.errors.${code}`,
    fallback: data.error || (err && err.message) || "Error",
    status: err && err.response ? err.response?.status : undefined,
  };
}

function findIncluded(included, ref) {
  if (!included || !ref) return null;
  return included.find((r) => r.type === ref.type && String(r.id) === String(ref.id)) || null;
}

// JSON:API resource → flat object. No-op if already flat (no .attributes).
export function deserialize(resource, included = []) {
  if (!resource || !resource.attributes) return resource;
  const flat = { id: Number(resource.id), ...resource.attributes };
  const rels = resource.relationships || {};
  Object.keys(rels).forEach((name) => {
    const relData = rels[name].data;
    if (Array.isArray(relData)) {
      flat[name] = relData.map((ref) => { const inc = findIncluded(included, ref); return inc ? deserialize(inc, included) : { id: Number(ref.id) }; });
    } else if (relData) {
      flat[`${name}_id`] = Number(relData.id);
      const inc = findIncluded(included, relData);
      if (inc) flat[name] = deserialize(inc, included);
    } else {
      flat[`${name}_id`] = null;
    }
  });
  return flat;
}

export function deserializeList(doc) {
  return (doc.data || []).map((r) => deserialize(r, doc.included));
}

// Attach Authorization explicitly (matches the app's per-call pattern, e.g.
// teletype/payment actions in store.js). axios.defaults.headers.common.Authorization
// is only set inside setUserInfo at login and is NOT restored on reload of a
// persisted session — so relying on the default caused 401 on the first real CRM
// call after a refresh. localStorage "auth_token" already holds the "Bearer …" value.
export function authCfg(extra) {
  const token = localStorage.getItem("auth_token");
  return { ...(extra || {}), headers: { ...(extra && extra.headers), Authorization: token } };
}

export function parseFreeCars(data) {
  // F-2: non-empty → { cars: { data: [...] } }; empty/invalid → { cars: [] } (bare array).
  const rows = (data && data.cars && data.cars.data) || [];
  return {
    cars: rows.map((r) => ({ id: Number(r.id), ...r.attributes })),
    total_count: (data && data.total_count) || 0,
    rental_days: data && data.rental_days,
  };
}

export default {
  // F-1: root /api/v1/, NOT under BASE (=/api/v1/crm). F-3: сортировку не шлём —
  // у v2 дефолт average_price asc, тот же порядок, что отдавал legacy.
  // FT-137: оба клиента поиска свободных переведены на free_cars_for_rent_v2 (без
  // Meilisearch). Старый роут оставлен живым для мобильных приложений.
  fetchFreeCars: ({ start_date, end_date, page = 1, per_page = 50 }) =>
    axios.post(`/api/v1/free_cars_for_rent_v2`, { start_date, end_date, page, per_page }, authCfg()).then((r) => parseFreeCars(r.data)),
  fetchPipelines: () => axios.get(`${BASE}/pipelines`, authCfg({ params: { include: "pipeline_stages" } })).then((r) => ({ items: deserializeList(r.data) })),
  fetchLeadSources: () => axios.get(`${BASE}/lead_sources`, authCfg()).then((r) => ({ items: deserializeList(r.data) })),
  fetchUsers: () => axios.get(`/api/v1/users`, authCfg()).then((r) => ({ items: r.data || [] })),
  fetchLeads: (params) => axios.get(`${BASE}/leads`, authCfg({ params })).then((r) => ({ items: deserializeList(r.data), meta: r.data.meta || {} })),
  fetchInbox: (params) => axios.get(`${BASE}/leads/inbox`, authCfg({ params })).then((r) => ({ items: deserializeList(r.data), meta: r.data.meta || {} })),
  // Доска одним запросом: { stages: { "<id>": { data:[...], meta } } } → { stages: { id: { items, meta } } }
  fetchBoard: (params) => axios.get(`${BASE}/leads/board`, authCfg({ params })).then((r) => {
    const out = {};
    const stages = (r.data && r.data.stages) || {};
    Object.keys(stages).forEach((sid) => {
      out[sid] = { items: deserializeList({ data: stages[sid].data }), meta: stages[sid].meta || {} };
    });
    return { stages: out };
  }),
  fetchLead: (id) => axios.get(`${BASE}/leads/${id}`, authCfg({ params: { include: "client,booking,pipeline_stage,lead_source" } })).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  // Bundle карточки лида одним запросом (лид + первые страницы задач/активностей/диалога).
  // Каждая секция распакована в ту же форму, что и её отдельный fetch — консьюмеры не меняются.
  fetchLeadDetail: (id) => axios.get(`${BASE}/leads/${id}/detail`, authCfg()).then((r) => {
    const d = r.data;
    const msgMeta = (d.messages && d.messages.meta) || {};
    return {
      lead: deserialize(d.lead.data, d.lead.included),
      tasks: { items: deserializeList(d.tasks), meta: (d.tasks && d.tasks.meta) || {} },
      activities: { items: deserializeList(d.activities), meta: (d.activities && d.activities.meta) || {} },
      messages: {
        items: ((d.messages && d.messages.data) || []).map((m) => deserialize(m)),
        identity: msgMeta.identity || null,
        hasMore: !!msgMeta.has_more,
      },
    };
  }),
  createLead: (attrs) => axios.post(`${BASE}/leads`, { lead: attrs }, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  updateLead: (id, attrs) => axios.patch(`${BASE}/leads/${id}`, { lead: attrs }, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  advanceLead: (id, body) => axios.post(`${BASE}/leads/${id}/advance`, body, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  loseLead: (id, reason) => axios.post(`${BASE}/leads/${id}/lose`, { reason }, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  winLead: (id) => axios.post(`${BASE}/leads/${id}/win`, {}, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  // FT-145: replace — необязательный; без него поведение прежнее (мобильный лаг ~6 мес).
  attachBooking: (id, booking_id, replace = false) =>
    axios.post(`${BASE}/leads/${id}/attach_booking`, { booking_id, replace }, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  detachBooking: (id) => axios.post(`${BASE}/leads/${id}/detach_booking`, {}, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  // Узкий источник для диалога выбора: плоский { data: [...] }, не JSON:API.
  bookingOptions: (id, q) =>
    axios.get(`${BASE}/leads/${id}/booking_options`, authCfg({ params: q ? { q } : {} })).then((r) => r.data.data || []),
  convertLead: (id, body) => axios.post(`${BASE}/leads/${id}/convert`, body, authCfg()).then((r) => ({ lead: deserialize(r.data.lead.data, r.data.lead.included), booking: deserialize(r.data.booking.data, r.data.booking.included) })),
  convertToClient: (id, attrs) =>
    axios.post(`${BASE}/leads/${id}/convert_to_client`, { client: attrs }, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  archiveLead: (id) => axios.post(`${BASE}/leads/${id}/archive`, {}, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  unarchiveLead: (id) => axios.post(`${BASE}/leads/${id}/unarchive`, {}, authCfg()).then((r) => ({ lead: deserialize(r.data.data, r.data.included) })),
  // FT-059 pipeline/stage editor (all return the parent pipeline → UPSERT_PIPELINE).
  createPipeline: (attrs) => axios.post(`${BASE}/pipelines`, { pipeline: attrs }, authCfg()).then((r) => ({ pipeline: deserialize(r.data.data, r.data.included) })),
  updatePipeline: (id, attrs) => axios.patch(`${BASE}/pipelines/${id}`, { pipeline: attrs }, authCfg()).then((r) => ({ pipeline: deserialize(r.data.data, r.data.included) })),
  deletePipeline: (id) => axios.delete(`${BASE}/pipelines/${id}`, authCfg()).then(() => ({ id })),
  createStage: (pipelineId, attrs) => axios.post(`${BASE}/pipelines/${pipelineId}/pipeline_stages`, { pipeline_stage: attrs }, authCfg()).then((r) => ({ pipeline: deserialize(r.data.data, r.data.included) })),
  updateStage: (pipelineId, stageId, attrs) => axios.patch(`${BASE}/pipelines/${pipelineId}/pipeline_stages/${stageId}`, { pipeline_stage: attrs }, authCfg()).then((r) => ({ pipeline: deserialize(r.data.data, r.data.included) })),
  deleteStage: (pipelineId, stageId, targetId) => axios.delete(`${BASE}/pipelines/${pipelineId}/pipeline_stages/${stageId}`, authCfg({ params: targetId ? { target_stage_id: targetId } : {} })).then((r) => ({ pipeline: deserialize(r.data.data, r.data.included) })),
  reorderStages: (pipelineId, orderedIds) => axios.post(`${BASE}/pipelines/${pipelineId}/pipeline_stages/reorder`, { ordered_ids: orderedIds }, authCfg()).then((r) => ({ pipeline: deserialize(r.data.data, r.data.included) })),
  // FT-062 lead-sources editor (all return the branch source list → SET_LEAD_SOURCES).
  createSource: (attrs) => axios.post(`${BASE}/lead_sources`, { lead_source: attrs }, authCfg()).then((r) => ({ sources: deserializeList(r.data) })),
  updateSource: (id, attrs) => axios.patch(`${BASE}/lead_sources/${id}`, { lead_source: attrs }, authCfg()).then((r) => ({ sources: deserializeList(r.data) })),
  deleteSource: (id, targetId) => axios.delete(`${BASE}/lead_sources/${id}`, authCfg({ params: targetId ? { target_source_id: targetId } : {} })).then((r) => ({ sources: deserializeList(r.data) })),
  reorderSources: (orderedIds) => axios.post(`${BASE}/lead_sources/reorder`, { ordered_ids: orderedIds }, authCfg()).then((r) => ({ sources: deserializeList(r.data) })),
  updateUserAccess: (id, enabled) => axios.patch(`${BASE}/users/${id}/access`, { enabled }, authCfg()).then((r) => r.data),
  fetchLeadMessages: (id, { before, limit } = {}) =>
    axios.get(`${BASE}/leads/${id}/messages`, authCfg({ params: { before, limit } })).then((r) => ({
      items: (r.data.data || []).map((d) => deserialize(d)),
      identity: r.data.meta ? r.data.meta.identity : null,
      hasMore: !!(r.data.meta && r.data.meta.has_more),
    })),
  sendLeadReply: (id, body) =>
    axios.post(`${BASE}/leads/${id}/reply`, { body }, authCfg()).then((r) => ({ message: deserialize(r.data.data) })),
  // multipart: Content-Type с boundary axios выставит сам по FormData
  sendLeadFile: (id, file, caption) => {
    const fd = new FormData();
    fd.append("file", file);
    if (caption) fd.append("caption", caption);
    return axios.post(`${BASE}/leads/${id}/reply_file`, fd, authCfg()).then((r) => ({ message: deserialize(r.data.data) }));
  },
  linkChannelIdentity: (id, clientId) =>
    axios.patch(`${BASE}/channel_identities/${id}`, { client_id: clientId }, authCfg()).then((r) => r.data),

  // Activities (046a)
  fetchLeadActivities: (id, { page, perPage } = {}) =>
    axios.get(`${BASE}/leads/${id}/activities`, authCfg({ params: { page, per_page: perPage } }))
      .then((r) => ({ items: deserializeList(r.data), meta: r.data.meta || {} })),
  createLeadActivity: (id, payload) =>
    axios.post(`${BASE}/leads/${id}/activities`, { activity: payload }, authCfg())
      .then((r) => ({ activity: deserialize(r.data.data) })),
  deleteLeadActivity: (id, activityId) =>
    axios.delete(`${BASE}/leads/${id}/activities/${activityId}`, authCfg()),

  // Tasks (046b)
  fetchLeadTasks: (id, { page, perPage } = {}) =>
    axios.get(`${BASE}/leads/${id}/tasks`, authCfg({ params: { page, per_page: perPage } }))
      .then((r) => ({ items: deserializeList(r.data), meta: r.data.meta || {} })),
  createLeadTask: (id, payload) =>
    axios.post(`${BASE}/leads/${id}/tasks`, { task: payload }, authCfg()).then((r) => ({ task: deserialize(r.data.data) })),
  updateLeadTask: (id, taskId, payload) =>
    axios.patch(`${BASE}/leads/${id}/tasks/${taskId}`, { task: payload }, authCfg()).then((r) => ({ task: deserialize(r.data.data) })),
  completeLeadTask: (id, taskId) =>
    axios.post(`${BASE}/leads/${id}/tasks/${taskId}/complete`, {}, authCfg()).then((r) => ({ task: deserialize(r.data.data) })),
  reopenLeadTask: (id, taskId) =>
    axios.post(`${BASE}/leads/${id}/tasks/${taskId}/reopen`, {}, authCfg()).then((r) => ({ task: deserialize(r.data.data) })),
  stopRecurrence: (id, taskId) =>
    axios.post(`${BASE}/leads/${id}/tasks/${taskId}/stop_recurrence`, {}, authCfg()).then((r) => ({ task: deserialize(r.data.data) })),
  deleteLeadTask: (id, taskId) => axios.delete(`${BASE}/leads/${id}/tasks/${taskId}`, authCfg()),
  fetchMyTasks: ({ assignee, status, page, perPage } = {}) =>
    axios.get(`${BASE}/tasks`, authCfg({ params: { assignee, status, page, per_page: perPage } }))
      .then((r) => ({ items: deserializeList(r.data), meta: r.data.meta || {} })),
  fetchCrmUsers: () => axios.get(`${BASE}/users`, authCfg()).then((r) => (r.data.data || [])),
  // FT-065: mark lead read (clears unread dot)
  markLeadRead: (id) => axios.patch(`${BASE}/leads/${id}/read`, {}, authCfg()),
  // FT-093 REQ-06: read-sync треда в Avito. Бэк отвечает 200 и на сбой провайдера
  // ({ synced:false, code:"avito_read_failed" }) — это НЕ ошибка UI, поэтому отдаём
  // тело как есть, без интерпретации.
  syncAvitoRead: (id) => axios.post(`${BASE}/leads/${id}/avito/read`, {}, authCfg()).then((r) => r.data),
  // FT-093 REQ-08: чёрный список Avito (ADMIN-ONLY на бэке: не-админу 403 admin_required).
  // reason_id: 1 спам, 2 мошенничество, 3 оскорбления, 4 другое.
  blacklistAvitoLead: (id, reasonId) =>
    axios.post(`${BASE}/leads/${id}/avito/blacklist`, { reason_id: reasonId }, authCfg()).then((r) => r.data),
};
